import { MeshTransmissionMaterial, useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import type { ReactElement } from 'react';
import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import type { GLTF } from 'three-stdlib';
import { bezier, reMap } from '../../../util/math.utils';
import useWindowSize from '../../../hooks/useWindowSize';
import { isTouch } from '../../../hooks/useIsDesktopDevice';

type GLTFResult = GLTF & {
  nodes: {
    Left: THREE.Mesh;
    Right: THREE.Mesh;
    BG: THREE.Mesh;
  };
  materials: {
    ['RS Material.2']: THREE.MeshStandardMaterial;
    ['RS Material']: THREE.MeshStandardMaterial;
  };
};

type PropsType = {
  progressRef: React.MutableRefObject<number>;
  backgroundColor?: string;
  shapeColor?: string;
  homepage?: boolean;
  isCurrent?: boolean;
};

export function TsukiMoon(props: PropsType): ReactElement {
  const group = useRef<THREE.Group>(null!);
  const { nodes, animations } = useGLTF('/assets/3d/Shapes/TsukiMoon.glb') as unknown as GLTFResult;
  const { width } = useWindowSize();

  const mixer = useRef<THREE.AnimationMixer>(undefined);
  const meshGroupRef = useRef<THREE.Group>(null);

  useEffect(() => {
    if (!group.current) return;
    mixer.current = new THREE.AnimationMixer(group.current);
    const action = mixer.current.clipAction(animations[0]);
    action.play();
  }, [animations]);

  useFrame(() => {
    if (!mixer?.current || !props.progressRef || !props.isCurrent || !meshGroupRef.current) return;
    if (props.progressRef.current < 100) {
      const p = props.progressRef.current;
      const b = bezier(p / 100, 0, 0.5, 0.5, 1);
      const t_bezier = b * animations[0].duration;
      const t = (p * animations[0].duration) / 100;
      mixer.current.setTime(props.homepage ? t_bezier : t);

      meshGroupRef.current.position.z = reMap(props.progressRef.current, 0, 100, 30, -10);
    }
  });

  const material = props.homepage ? (
    <MeshTransmissionMaterial
      backside
      samples={1}
      iridescence={1}
      iridescenceIOR={0.65}
      backsideResolution={512}
      iridescenceThicknessRange={[0, 200]}
      envMapIntensity={0.7}
      background={new THREE.Color('#4C4744')}
      distortion={0}
      distortionScale={0}
      temporalDistortion={0}
      clearcoat={1}
    />
  ) : (
    <MeshTransmissionMaterial
      backside
      transparent
      samples={1}
      iridescence={isTouch ? 0 : 1}
      iridescenceIOR={isTouch ? 0 : 0.65}
      backsideThickness={0}
      backsideResolution={isTouch ? 256 : 512}
      iridescenceThicknessRange={[0, 200]}
      clearcoat={isTouch ? 0.3 : 0.4}
      envMapIntensity={isTouch ? 1 : 2}
      opacity={0.85}
      transmission={1}
      chromaticAberration={0}
      transmissionSampler={false}
      roughness={isTouch ? 0.2 : 0}
      clearcoatRoughness={0}
      reflectivity={isTouch ? 0.5 : 1}
      sheenColor="#f29a9a"
      sheen={isTouch ? 0 : 1}
      metalness={0}
      ior={1}
      specularColor="white"
      specularIntensity={isTouch ? 0.2 : 0.3}
      attenuationColor="white"
      thickness={isTouch ? 0.05 : 0.1}
      anisotropicBlur={isTouch ? 0 : 0.1}
      distortion={0}
      distortionScale={0}
      temporalDistortion={0}
      background={new THREE.Color(props.shapeColor || '#4C4744')}
    />
  );

  return (
    <group ref={group} {...props} dispose={null} visible={props.isCurrent}>
      <group ref={meshGroupRef} position={[-1, 4, 0]}>
        <group
          name="Moon"
          position={[0, 0, -450]}
          rotation={[-0.873, 0, 0]}
          scale={Math.min(
            props.homepage ? 0.6 : 0.7,
            reMap(width, 320, 650, 0.3, props.homepage ? 0.6 : 0.7),
          )}
        >
          {props.homepage && (
            <mesh
              visible={false}
              geometry={nodes.Left.geometry}
              position={[4.163, -14.247, -0.392]}
              rotation={[-0.009, 0.11, 0.125]}
            >
              {material}
            </mesh>
          )}
          <mesh
            name="Left"
            geometry={nodes.Left.geometry}
            position={[4.163, -14.247, -0.392]}
            rotation={[-0.009, 0.11, 0.125]}
          >
            {material}
          </mesh>
          <mesh
            name="Right"
            geometry={nodes.Right.geometry}
            position={[-5.153, -3.259, 0]}
            rotation={[0, 0, -Math.PI]}
          >
            {material}
          </mesh>
        </group>
      </group>
    </group>
  );
}

useGLTF.preload('/assets/3d/Shapes/TsukiMoon.glb');

export default TsukiMoon;
